import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
    children: ReactNode;
    /** Shown in the fallback header so it's clear which area failed. */
    label?: string;
}

interface State {
    error: Error | null;
    /**
     * Bumped by "Reintentar" and used as the key of the children, so a retry remounts the subtree
     * from scratch instead of re-rendering into DOM the crash may have left inconsistent (the
     * `removeChild` NotFoundError that a browser page-translator provokes is exactly that case).
     */
    generation: number;
}

/**
 * Catches render errors in its subtree so one broken component shows a readable fallback instead of
 * white-screening the whole app. The error message is rendered on screen ON PURPOSE — it's the only
 * way to diagnose crashes that only reproduce on specific accounts we can't inspect directly.
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null, generation: 0 };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Full stack for whoever can open the console; the on-screen message covers the rest.
        console.error('ErrorBoundary caught a render error', error, info.componentStack);
    }

    private reset = () => this.setState((prev) => ({ error: null, generation: prev.generation + 1 }));

    render() {
        const { error, generation } = this.state;
        if (!error) return <Fragment key={generation}>{this.props.children}</Fragment>;

        return (
            <div className="flex flex-1 items-center justify-center p-6">
                <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-xs">
                    <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-400 dark:ring-red-800/60">
                        <AlertTriangle className="size-6" />
                    </div>
                    <p className="text-base font-medium text-foreground">
                        {this.props.label ?? 'Se rompió esta sección'}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Recargá la página. Si sigue pasando, pasale este mensaje al equipo:
                    </p>
                    <pre className="mt-3 max-h-40 overflow-auto rounded-lg border border-border bg-muted/50 p-2.5 text-left font-mono text-[11px] leading-snug text-foreground whitespace-pre-wrap break-words">
                        {error.message || String(error)}
                    </pre>
                    <div className="mt-5 flex justify-center gap-2">
                        <Button variant="outline" size="sm" onClick={this.reset}>
                            Reintentar
                        </Button>
                        <Button size="sm" onClick={() => window.location.reload()}>
                            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                            Recargar
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}
